import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, CloudUpload, Download, Menu as MenuIcon, ScrollText, Server, Smartphone } from "lucide-react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { usePOS } from "@/context/POSContext";
import { useInteractionLog } from "@/hooks/useInteractionLog";
import { businessDateFor } from "@/lib/interactionLog/businessDate";
import { downloadDayFromDevice, downloadDayFromServer } from "@/lib/interactionLog/downloads";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function timeAgo(iso, now = Date.now()) {
  if (!iso) return null;
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

function dayLabel(businessDate) {
  const today = businessDateFor();
  const yesterday = businessDateFor(new Date(Date.now() - 24 * 60 * 60 * 1000));
  if (businessDate === today) return "Today";
  if (businessDate === yesterday) return "Yesterday";
  const [year, month, day] = businessDate.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

/**
 * Logging, on this device. Reached from the captain's menu and from the main sidebar.
 *
 * Logging is on by default; switching it off is allowed but said plainly, because a
 * problem on a device with logging off leaves nothing to trace. Days turn over at 3am.
 * A day's entries stay on the device until the day is over AND they are uploaded.
 */
export default function LoggingPage() {
  const { token } = usePOS();
  const navigate = useNavigate();
  const inCaptain = useLocation().pathname.startsWith("/captain");
  const openDrawer = useOutletContext()?.openDrawer;
  const log = useInteractionLog();
  const [serverDays, setServerDays] = useState([]);
  const [serverError, setServerError] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/logs/dates`, { headers: { Authorization: `Bearer ${token}` } })
      .then(response => {
        if (!cancelled) {
          setServerDays(Array.isArray(response.data) ? response.data : []);
          setServerError(null);
        }
      })
      .catch(error => {
        if (!cancelled) setServerError(error?.response?.status ? `The server answered ${error.response.status}.` : "The server could not be reached.");
      });
    return () => {
      cancelled = true;
    };
  }, [token, log.lastSyncAt]);

  const days = useMemo(() => {
    const byDate = new Map();
    for (const day of log.days || []) byDate.set(day.businessDate, { businessDate: day.businessDate, device: day, server: null });
    for (const day of serverDays) {
      const slot = byDate.get(day.business_date) || { businessDate: day.business_date, device: null, server: null };
      slot.server = day;
      byDate.set(day.business_date, slot);
    }
    return [...byDate.values()].sort((a, b) => (a.businessDate < b.businessDate ? 1 : -1));
  }, [log.days, serverDays]);

  const run = async (key, work, done) => {
    setBusy(key);
    try {
      const result = await work();
      if (done) done(result);
    } catch (error) {
      toast.error(error?.message || "That did not work.");
    } finally {
      setBusy(null);
    }
  };

  const uploadNow = () =>
    run("sync", log.syncNow, result => {
      if (result?.status === "ok") {
        toast.success(result.acked ? `Uploaded ${result.acked} entries` : "Nothing waiting — all uploaded");
      } else if (result?.status === "skipped") {
        toast.error("Not signed in — logs stay on this device until someone signs in.");
      } else {
        toast.error(`Upload failed: ${result?.error || "unknown error"}`);
      }
    });

  return (
    <div className="h-full overflow-y-auto bg-background" data-testid="logging-page">
      {inCaptain && (
        // CaptainLayout gives its pages no chrome of their own; every other pad
        // screen draws this bar, and without it the page was a dead end.
        <header className="min-h-[3.5rem] pt-safe flex items-center gap-3 px-4 bg-primary text-primary-foreground shadow-md sticky top-0 z-20">
          {openDrawer && (
            <button
              className="w-11 h-11 -ml-2 flex items-center justify-center rounded-md hover:bg-primary-foreground/20"
              onClick={openDrawer}
              aria-label="Open mobile menu"
              data-testid="logging-menu-button"
            >
              <MenuIcon className="w-6 h-6" />
            </button>
          )}
          <button
            className="flex items-center gap-2 h-11 px-2 rounded-md hover:bg-primary-foreground/20 text-body font-semibold"
            onClick={() => navigate("/captain")}
            data-testid="logging-back-to-tables"
          >
            <ArrowLeft className="w-5 h-5" /> Tables
          </button>
        </header>
      )}
      <div className="mx-auto max-w-2xl space-y-base p-base">
        {/* In the app shell the header already says "Logging"; the pad has no such
            header, so there the page keeps its own title (screen review #14). */}
        <header className="flex items-start gap-snug">
          {inCaptain && <ScrollText className="mt-1 h-6 w-6 shrink-0 text-primary" />}
          <div>
            <h1 className={inCaptain ? "text-title font-semibold" : "sr-only"}>Logging</h1>
            <p className="text-body text-muted-foreground">
              Keeps a record of what happens on this device so problems can be traced and fixed.
            </p>
          </div>
        </header>

        <section className="space-y-snug rounded-lg border bg-card p-base">
          <div className="flex items-center justify-between gap-base">
            <div>
              <div className="text-body font-semibold">Detailed logging</div>
              <div className="text-micro text-muted-foreground">Every tap, field, swipe and connection on this device.</div>
            </div>
            <Switch checked={log.enabled} onCheckedChange={log.setEnabled} data-testid="logging-enabled-switch" aria-label="Detailed logging" />
          </div>
          {!log.enabled && (
            <div role="alert" data-testid="logging-off-warning" className="flex gap-snug rounded-md bg-warning/10 p-snug text-micro text-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Logging is off on this device. If something goes wrong here, there will be no record of it.
            </div>
          )}
        </section>

        <section className="space-y-snug rounded-lg border bg-card p-base" data-testid="logging-sync-status">
          <div className="grid grid-cols-2 gap-snug text-body">
            <div>
              <div className="text-micro text-muted-foreground">Waiting to upload</div>
              <div className="font-semibold" data-testid="logging-waiting-count">{log.waiting}</div>
            </div>
            <div>
              <div className="text-micro text-muted-foreground">Last uploaded</div>
              <div className="font-semibold" data-testid="logging-last-sync">{timeAgo(log.lastSyncAt) || "Not yet"}</div>
            </div>
          </div>
          {log.refused > 0 && (
            <div role="alert" data-testid="logging-refused" className="rounded-md bg-warning/10 p-snug text-micro text-warning">
              {log.refused} {log.refused === 1 ? "entry was" : "entries were"} refused by the server. They are kept on this device and can be downloaded below.
            </div>
          )}
          {log.lastSyncError && (
            <div role="alert" data-testid="logging-sync-error" className="rounded-md bg-destructive/10 p-snug text-micro text-destructive">
              Last upload failed {timeAgo(log.lastSyncError.at)}: {log.lastSyncError.message}
            </div>
          )}
          {log.storeError && (
            <div role="alert" data-testid="logging-store-error" className="rounded-md bg-destructive/10 p-snug text-micro text-destructive">
              This device could not save its log: {log.storeError}
            </div>
          )}
          <Button variant="action" onClick={uploadNow} disabled={log.syncing || busy === "sync"} className="w-full" data-testid="logging-sync-now">
            <CloudUpload className="mr-2 h-4 w-4" />
            {log.syncing || busy === "sync" ? "Uploading…" : "Upload now"}
          </Button>
        </section>

        <section className="space-y-snug rounded-lg border bg-card p-base" data-testid="logging-days">
          <div>
            <h2 className="text-body font-bold">Logs by day</h2>
            <p className="text-micro text-muted-foreground">A day runs from 3am to 3am, so a late shift stays together.</p>
          </div>
          {serverError && <p className="text-micro text-destructive" data-testid="logging-server-error">Server copies unavailable: {serverError}</p>}
          {days.length === 0 ? (
            <p className="text-micro text-muted-foreground" data-testid="logging-no-days">Nothing recorded yet.</p>
          ) : (
            <ul className="divide-y">
              {days.map(({ businessDate, device, server }) => (
                <li key={businessDate} className="flex flex-wrap items-center justify-between gap-snug py-snug" data-testid={`logging-day-${businessDate}`}>
                  <div>
                    <div className="text-body font-semibold">{dayLabel(businessDate)}</div>
                    <div className="text-micro text-muted-foreground">
                      {device ? `${device.total} on this device${device.waiting ? ` · ${device.waiting} waiting` : " · all uploaded"}` : "Cleared from this device"}
                      {server ? ` · ${server.client} on server` : ""}
                    </div>
                  </div>
                  <div className="flex gap-tight">
                    {device && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy === `device-${businessDate}`}
                        onClick={() => run(`device-${businessDate}`, () => downloadDayFromDevice(businessDate))}
                        data-testid={`logging-download-device-${businessDate}`}
                      >
                        <Smartphone className="mr-1 h-4 w-4" />
                        <Download className="mr-1 h-3 w-3" />
                        Device
                      </Button>
                    )}
                    {server && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy === `server-${businessDate}`}
                        onClick={() => run(`server-${businessDate}`, () => downloadDayFromServer({ businessDate }))}
                        data-testid={`logging-download-server-${businessDate}`}
                      >
                        <Server className="mr-1 h-4 w-4" />
                        <Download className="mr-1 h-3 w-3" />
                        Server
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
